import { DataGridPro, GridApiPro, GridPaginationModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { GridBox, GridNoRowsOverlay } from "components/shared/grids";
import { GridToolbar } from "components/shared/grids";
import { Ico_SortAscending, Ico_SortDescending, Ico_MenuDots, Ico_Filter_Component, CentralDataGridPro, Ico_Filter_Active_Component } from "@sagecentral/core-ui";
import React, { useCallback, useEffect, useState } from "react";
import { useGridFilters, useGridMetadata, useGridSorting } from "hooks/shared";
import useRelatableWorkItems from "hooks/work-items/useRelatableWorkItems";
import useAddRelatedColumns from "./useAddRelatedColumns";
import { Box, CircularProgress } from "@mui/material";
import { IAddRelatedWorkItemEntry } from "hooks/work-items";
import { ISelectedWorkItemsGrid } from "./SelectedWorkItemsGrid";
import { customLocaleText } from "models/grids/HeaderFilterLocaleText";

const NoRowsOverlay = () => <GridNoRowsOverlay text={`No work items found.`} />

type WorkItemsSelectionGridProps = {
    apiRef: React.MutableRefObject<GridApiPro>;
    selectedRef: React.MutableRefObject<ISelectedWorkItemsGrid | null>;
    workItemId: number;
    workspaceId: number;
    draft?: boolean;
};

const WorkItemsSelectionGrid = (props: WorkItemsSelectionGridProps) => {
    const {
        apiRef,
        workItemId,
        workspaceId,
        draft,
        selectedRef
    } = props;

    const [selected, setSelected] = useState<GridRowSelectionModel>([]);

    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        pageSize: 25,
        page: 0
    });

    const [filter, filterChange, filterModel] = useGridFilters({});
    const [sort, sortChange, sortModel] = useGridSorting({});

    const gridMetadata = useGridMetadata(filter, sort, undefined, paginationModel);

    const [workItems, loading, count] = useRelatableWorkItems(workItemId, workspaceId, gridMetadata, draft);

    const [
        columns
    ] = useAddRelatedColumns(true);

    useEffect(() => {
        handleSelectionChanged([]);
    }, [workItemId, workspaceId]);

    const handleSelectionChanged = (model: GridRowSelectionModel) => {
        setSelected(model);
        selectedRef.current?.setSelection(model, workItems);
    };

    const getData = () => loading ? [] : workItems;
    const isFilterApplied = () => (gridMetadata?.filterParam?.filters.length || 0) > 0;

    return (
        <GridBox getData={getData}>
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxHeight: '450px'
                }}
                role="region"
                aria-label="Available work items selection grid"
            >
                <DataGridPro
                    apiRef={apiRef}
                    getRowId={row => row.id}
                    loading={loading}
                    columns={columns}
                    rows={getData()}
                    rowCount={count}
                    pagination
                    paginationMode='server'
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    checkboxSelection
                    onRowSelectionModelChange={handleSelectionChanged}
                    rowSelectionModel={selected}
                    disableRowSelectionOnClick
                    filterMode='server'
                    filterModel={filterModel}
                    filterDebounceMs={500}
                    onFilterModelChange={filterChange}
                    sortingMode='server'
                    sortModel={sortModel}
                    onSortModelChange={sortChange}
                    keepNonExistentRowsSelected
                    slotProps={{
                        panel: {
                            placement: 'bottom-end'
                        },
                        baseButton: {
                            tabIndex: 0
                        },
                        cell: {
                            tabIndex: 0
                        },
                        columnHeaders: {
                            'aria-label': 'Work items grid column headers',
                            tabIndex: 0
                        }
                    }}
                    slots={{
                        loadingOverlay: () => (<CircularProgress />),
                        columnMenuSortAscendingIcon: Ico_SortAscending,
                        columnMenuSortDescendingIcon: Ico_SortDescending,
                        columnSortedAscendingIcon: Ico_SortAscending,
                        columnSortedDescendingIcon: Ico_SortDescending,
                        columnUnsortedIcon: undefined,
                        moreActionsIcon: Ico_MenuDots,
                        openFilterButtonIcon: isFilterApplied() ? Ico_Filter_Active_Component : Ico_Filter_Component,
                        quickFilterIcon: Ico_Filter_Component,
                        columnFilteredIcon: Ico_Filter_Component,
                        quickFilterClearIcon: Ico_Filter_Component,
                        columnMenuFilterIcon: Ico_Filter_Component,
                        noRowsOverlay: () => NoRowsOverlay(),
                    }}
                    headerFilters
                    localeText={customLocaleText}
                    getRowClassName={(params: any) =>
                        params.indexRelativeToCurrentPage % 2 === 0 ? 'row even' : 'row odd'
                    }
                />
            </Box>
        </GridBox>
    )
};

export default WorkItemsSelectionGrid;